import React, { useState, useEffect, useRef } from 'react';

const BocciaTimer = () => {
  const [redScore, setRedScore] = useState(0);
  const [blueScore, setBlueScore] = useState(0);
  const [endCount, setEndCount] = useState(0);
  const [gameTimeMinutes, setGameTimeMinutes] = useState(6);
  const [redTimeLeft, setRedTimeLeft] = useState(6 * 60); // in seconds
  const [blueTimeLeft, setBlueTimeLeft] = useState(6 * 60); // in seconds
  const [activeTimer, setActiveTimer] = useState(null); // 'red', 'blue', or null
  const [redMedicalTimeout, setRedMedicalTimeout] = useState(0); // medical timeout in seconds
  const [blueMedicalTimeout, setBlueMedicalTimeout] = useState(0); // medical timeout in seconds
  const [activeMedicalTimers, setActiveMedicalTimers] = useState({ red: false, blue: false });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const intervalRef = useRef(null);
  const medicalIntervalRef = useRef(null);

  // Function to play beep sound
  const playBeep = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800Hz frequency
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime); // Volume
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1); // Fade out
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 1); // 1 second beep
    } catch (error) {
      console.log('Audio not supported or blocked:', error);
    }
  };

  useEffect(() => {
    if (activeTimer) {
      intervalRef.current = setInterval(() => {
        if (activeTimer === 'red') {
          setRedTimeLeft(prev => {
            if (prev <= 1) {
              setActiveTimer(null); // Stop timer when it reaches 0
              return 0;
            }
            return prev - 1;
          });
        } else if (activeTimer === 'blue') {
          setBlueTimeLeft(prev => {
            if (prev <= 1) {
              setActiveTimer(null); // Stop timer when it reaches 0
              return 0;
            }
            return prev - 1;
          });
        }
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [activeTimer]);

  // Medical timeout timer effect
  useEffect(() => {
    if (activeMedicalTimers.red || activeMedicalTimers.blue) {
      medicalIntervalRef.current = setInterval(() => {
        if (activeMedicalTimers.red) {
          setRedMedicalTimeout(prev => {
            if (prev <= 1) {
              setActiveMedicalTimers(current => ({ ...current, red: false }));
              playBeep(); // Play beep when Red medical timeout finishes
              return 0;
            }
            return prev - 1;
          });
        }
        if (activeMedicalTimers.blue) {
          setBlueMedicalTimeout(prev => {
            if (prev <= 1) {
              setActiveMedicalTimers(current => ({ ...current, blue: false }));
              playBeep(); // Play beep when Blue medical timeout finishes
              return 0;
            }
            return prev - 1;
          });
        }
      }, 1000);
    } else {
      clearInterval(medicalIntervalRef.current);
    }

    return () => clearInterval(medicalIntervalRef.current);
  }, [activeMedicalTimers]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const adjustScore = (team, change) => {
    if (team === 'red') {
      setRedScore(prev => Math.max(0, prev + change));
    } else {
      setBlueScore(prev => Math.max(0, prev + change));
    }
  };

  const adjustTime = (team, type, change) => {
    if (team === 'red') {
      if (type === 'minutes') {
        setRedTimeLeft(prev => Math.max(0, prev + (change * 60)));
      } else {
        setRedTimeLeft(prev => Math.max(0, prev + change));
      }
    } else {
      if (type === 'minutes') {
        setBlueTimeLeft(prev => Math.max(0, prev + (change * 60)));
      } else {
        setBlueTimeLeft(prev => Math.max(0, prev + change));
      }
    }
  };

  const resetTimes = () => {
    setRedTimeLeft(gameTimeMinutes * 60);
    setBlueTimeLeft(gameTimeMinutes * 60);
    setActiveTimer(null);
  };

  const resetScores = () => {
    setRedScore(0);
    setBlueScore(0);
  };

  const resetEnds = () => {
    setEndCount(0);
  };

  const toggleTimer = (team) => {
    if (activeTimer === team) {
      // Stop the current timer
      setActiveTimer(null);
    } else {
      // Start the selected timer (this automatically stops any other running timer)
      setActiveTimer(team);
    }
  };

  const startMedicalTimeout = (team) => {
    if (team === 'red') {
      setRedMedicalTimeout(10 * 60); // 10 minutes in seconds
      setActiveMedicalTimers(current => ({ ...current, red: true }));
    } else {
      setBlueMedicalTimeout(10 * 60); // 10 minutes in seconds
      setActiveMedicalTimers(current => ({ ...current, blue: true }));
    }
  };

  const stopMedicalTimeout = (team) => {
    if (team === 'red') {
      setActiveMedicalTimers(current => ({ ...current, red: false }));
      setRedMedicalTimeout(0);
    } else {
      setActiveMedicalTimers(current => ({ ...current, blue: false }));
      setBlueMedicalTimeout(0);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(() => {
        // Fullscreen not supported
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  return (
    <div className="boccia-timer min-h-screen bg-black flex flex-col font-mono">
      {/* Top Control Bar */}
      <div className="bg-black text-white p-1 flex items-center gap-3 text-xs border-b border-gray-700">
        <div className="flex items-center gap-1">
          <span>End: {endCount}</span>
          <button 
            className="bg-gray-600 hover:bg-gray-500 text-white px-1 py-0.5 text-xs border border-gray-500"
            onClick={() => setEndCount(prev => Math.max(0, prev - 1))}
          >
            -
          </button>
          <button 
            className="bg-gray-600 hover:bg-gray-500 text-white px-1 py-0.5 text-xs border border-gray-500"
            onClick={() => setEndCount(prev => prev + 1)}
          >
            +
          </button>
        </div>
        
        <div className="flex items-center gap-1">
          <label>Game Time (minutes):</label>
          <input 
            type="number" 
            value={gameTimeMinutes}
            onChange={(e) => setGameTimeMinutes(parseInt(e.target.value) || 6)}
            className="w-10 h-5 text-xs bg-white text-black border border-gray-400 px-1"
            min="1"
          />
        </div>
        
        <button 
          onClick={resetTimes}
          className="bg-gray-500 hover:bg-gray-400 text-white px-2 py-0.5 text-xs border border-gray-400"
        >
          Reset Times
        </button>
        
        <button 
          onClick={resetScores}
          className="bg-gray-500 hover:bg-gray-400 text-white px-2 py-0.5 text-xs border border-gray-400"
        >
          Reset Scores
        </button>
        
        <button 
          onClick={resetEnds}
          className="bg-gray-500 hover:bg-gray-400 text-white px-2 py-0.5 text-xs border border-gray-400"
        >
          Reset Ends
        </button>
      </div>

      {/* Main Timer Display */}
      <div className="flex-1 flex">
        {/* Red Team Side */}
        <div className="w-1/2 bg-red-600 flex flex-col items-center justify-center text-white relative" style={{backgroundColor: '#FF0000'}}>
          {/* Score Display */}
          <div className="absolute top-4 left-4">
            <h1 className="text-5xl font-bold">Red: {redScore}</h1>
          </div>
          
          {/* Score Control Buttons */}
          <div className="absolute top-24 left-4 flex gap-2">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                adjustScore('red', -1);
              }}
              className="border-2 border-white bg-transparent text-white text-3xl font-bold w-20 h-14 hover:bg-white hover:text-red-600 transition-colors"
              data-testid="red-minus"
            >
              -
            </button>
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                adjustScore('red', 1);
              }}
              className="border-2 border-white bg-transparent text-white text-3xl font-bold w-20 h-14 hover:bg-white hover:text-red-600 transition-colors"
              data-testid="red-plus"
            >
              +
            </button>
          </div>

          {/* Large Timer Display */}
          <div className="text-9xl font-bold text-white select-none tracking-wider">
            {formatTime(redTimeLeft)}
          </div>

          {/* Timer Controls */}
          <div className="absolute bottom-12 left-4 flex gap-1">
            <button 
              onClick={() => adjustTime('red', 'minutes', -1)}
              className="bg-transparent border border-white text-white px-2 py-1 text-xs hover:bg-white hover:text-red-600 transition-colors"
            >
              Minutes-
            </button>
            <button 
              onClick={() => adjustTime('red', 'minutes', 1)}
              className="bg-transparent border border-white text-white px-2 py-1 text-xs hover:bg-white hover:text-red-600 transition-colors"
            >
              Minutes+
            </button>
            <button 
              onClick={() => adjustTime('red', 'seconds', -1)}
              className="bg-transparent border border-white text-white px-2 py-1 text-xs hover:bg-white hover:text-red-600 transition-colors"
            >
              Seconds-
            </button>
            <button 
              onClick={() => adjustTime('red', 'seconds', 1)}
              className="bg-transparent border border-white text-white px-2 py-1 text-xs hover:bg-white hover:text-red-600 transition-colors"
            >
              Seconds+
            </button>
          </div>

          {/* Play/Pause Button */}
          <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2">
            <button 
              onClick={() => toggleTimer('red')}
              className={`bg-transparent border-2 border-white text-white px-6 py-2 text-lg font-bold transition-colors ${
                activeTimer === 'red' 
                  ? 'bg-white text-red-600' 
                  : 'hover:bg-white hover:text-red-600'
              }`}
            >
              Play/Pause
            </button>
          </div>

          {/* Medical Timeout Section */}
          <div className="absolute top-1/2 left-4 transform -translate-y-1/2">
            <div className="flex flex-col items-center gap-2">
              <button 
                onClick={() => activeMedicalTimers.red ? stopMedicalTimeout('red') : startMedicalTimeout('red')}
                className={`bg-transparent border-2 border-white text-white px-4 py-2 text-sm font-bold transition-colors flex items-center gap-2 ${
                  activeMedicalTimers.red 
                    ? 'bg-white text-red-600' 
                    : 'hover:bg-white hover:text-red-600'
                }`}
              >
                <span className="text-lg">🩹</span>
                Medical Timeout
              </button>
              {redMedicalTimeout > 0 && (
                <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded text-lg font-bold">
                  {formatTime(redMedicalTimeout)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Blue Team Side */}
        <div className="w-1/2 bg-blue-600 flex flex-col items-center justify-center text-white relative" style={{backgroundColor: '#0000FF'}}>
          {/* Score Display */}
          <div className="absolute top-4 right-4">
            <h1 className="text-5xl font-bold">Blue: {blueScore}</h1>
          </div>
          
          {/* Score Control Buttons */}
          <div className="absolute top-24 right-4 flex gap-2">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                adjustScore('blue', -1);
              }}
              className="border-2 border-white bg-transparent text-white text-3xl font-bold w-20 h-14 hover:bg-white hover:text-blue-600 transition-colors"
              data-testid="blue-minus"
            >
              -
            </button>
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                adjustScore('blue', 1);
              }}
              className="border-2 border-white bg-transparent text-white text-3xl font-bold w-20 h-14 hover:bg-white hover:text-blue-600 transition-colors"
              data-testid="blue-plus"
            >
              +
            </button>
          </div>

          {/* Large Timer Display */}
          <div className="text-9xl font-bold text-white select-none tracking-wider">
            {formatTime(blueTimeLeft)}
          </div>

          {/* Timer Controls */}
          <div className="absolute bottom-12 right-4 flex gap-1">
            <button 
              onClick={() => adjustTime('blue', 'minutes', 1)}
              className="bg-transparent border border-white text-white px-2 py-1 text-xs hover:bg-white hover:text-blue-600 transition-colors"
            >
              Minutes+
            </button>
            <button 
              onClick={() => adjustTime('blue', 'minutes', -1)}
              className="bg-transparent border border-white text-white px-2 py-1 text-xs hover:bg-white hover:text-blue-600 transition-colors"
            >
              Minutes-
            </button>
            <button 
              onClick={() => adjustTime('blue', 'seconds', 1)}
              className="bg-transparent border border-white text-white px-2 py-1 text-xs hover:bg-white hover:text-blue-600 transition-colors"
            >
              Seconds+
            </button>
            <button 
              onClick={() => adjustTime('blue', 'seconds', -1)}
              className="bg-transparent border border-white text-white px-2 py-1 text-xs hover:bg-white hover:text-blue-600 transition-colors"
            >
              Seconds-
            </button>
          </div>

          {/* Play/Pause Button */}
          <div className="absolute bottom-12 right-1/2 transform translate-x-1/2">
            <button 
              onClick={() => toggleTimer('blue')}
              className={`bg-transparent border-2 border-white text-white px-6 py-2 text-lg font-bold transition-colors ${
                activeTimer === 'blue' 
                  ? 'bg-white text-blue-600' 
                  : 'hover:bg-white hover:text-blue-600'
              }`}
            >
              Play/Pause
            </button>
          </div>

          {/* Medical Timeout Section */}
          <div className="absolute top-1/2 right-4 transform -translate-y-1/2">
            <div className="flex flex-col items-center gap-2">
              <button 
                onClick={() => activeMedicalTimers.blue ? stopMedicalTimeout('blue') : startMedicalTimeout('blue')}
                className={`bg-transparent border-2 border-white text-white px-4 py-2 text-sm font-bold transition-colors flex items-center gap-2 ${
                  activeMedicalTimers.blue 
                    ? 'bg-white text-blue-600' 
                    : 'hover:bg-white hover:text-blue-600'
                }`}
              >
                <span className="text-lg">🩹</span>
                Medical Timeout
              </button>
              {blueMedicalTimeout > 0 && (
                <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded text-lg font-bold">
                  {formatTime(blueMedicalTimeout)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-black text-white p-1 flex justify-between items-center text-xs">
        <button 
          onClick={toggleFullscreen}
          className="text-white hover:text-gray-300 transition-colors bg-gray-800 px-2 py-1 border border-gray-600"
        >
          Click Me To Go Fullscreen (f available)!
        </button>
        <div className="text-right">
          © ebb.hw
        </div>
      </div>
    </div>
  );
};

export default BocciaTimer;